import { forgotPassword } from '@/api/backend/account';
import { AuthForm } from '@/components/auth-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, FormError, FormField, FormLabel, FormLabelGroup, Input } from '@opaz/ui-components';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { SubmitHandler, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

export const Route = createFileRoute('/forgot-password/')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      redirect: search.redirect as string | undefined,
    };
  },
  head: () => {
    return {
      meta: [
        {
          title: 'Forgot Password - OPAZ E-Service Portal',
        },
      ],
    };
  },
});

type ForgotPasswordInput = {
  email: string;
};

const forgotPasswordFormSchema = z.object({
  email: z.string().email().min(1).max(100),
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { redirect: redirectUrl } = Route.useSearch();
  const [formError, setFormError] = useState<AxiosError | undefined>(undefined);
  const [isRequestSent, setIsRequestSent] = useState(false);

  const {
    formState: { errors },
    setValue,
    handleSubmit,
    control,
  } = useForm<ForgotPasswordInput>({
    values: {
      email: '',
    },
    resolver: zodResolver(forgotPasswordFormSchema),
  });

  const { field: emailField } = useController({
    name: 'email',
    control,
  });

  const { mutate: handleForgotPassword, isPending: isForgotPasswordLoading } = useMutation({
    mutationFn: async (data: { email: string }) => {
      console.debug('[/login]: Sending forgot password...');

      await forgotPassword(data.email);
    },
    onSuccess: () => {
      setIsRequestSent(true);
    },
    onError: (error: AxiosError) => {
      setFormError(error);
      console.debug('[/login]: Forgot password failed.');
    },
  });

  const onSubmit: SubmitHandler<ForgotPasswordInput> = async ({ email }) => {
    await handleForgotPassword({ email });
  };

  return (
    <AuthForm
      error={formError}
      onBack={() => {
        navigate({
          to: '/login',
          search: { redirect: redirectUrl },
        });
      }}
    >
      {isRequestSent ? (
        <div>
          <h1 className="font-bold text-3xl text-[#1A444F] mb-6">{t('forgotPassword.success.title')}</h1>
          <p className="text-xl text-muted-foreground">{t('forgotPassword.success.description')}</p>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-10">
            <div>
              <h1 className="font-bold text-3xl text-[#1A444F] mb-6">{t('forgotPassword.title')}</h1>
              <p className="text-xl text-muted-foreground">{t('forgotPassword.description')}</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <FormField>
                <FormLabelGroup>
                  <FormLabel error={!!errors.email}>{t('login.authMethods.email.fields.email.label')}</FormLabel>
                  {errors.email && <FormError>{t(errors.email.message as unknown as TemplateStringsArray)}</FormError>}
                </FormLabelGroup>

                <Input
                  disabled={isForgotPasswordLoading}
                  error={!!errors.email}
                  value={emailField.value}
                  onChange={emailField.onChange}
                  onClear={() => {
                    setValue('email', '');
                  }}
                ></Input>
              </FormField>

              <Button className="w-full my-10" type="submit" disabled={isForgotPasswordLoading}>
                {t('forgotPassword.actionButtonLabel')}
              </Button>
            </form>
          </div>
        </>
      )}
    </AuthForm>
  );
}
